import { useState } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import type { Evidence } from '@/types/api'

interface HiddenSourcesSectionProps {
  hiddenEvidences: Evidence[]
  getSourceLabel: (sourceId: string) => string
  onRestore: (sourceId: string) => void
}

const mdComponents: Components = {
  p: ({ children }) => <>{children}</>,
  em: ({ children }) => <i>{children}</i>,
}

const supportTagStyle: Record<string, string> = {
  supports:           'text-[var(--status-verified-text)]',
  partially_supports: 'text-[var(--status-partial-text)]',
  contradicts:        'text-[var(--status-error-text)]',
}

const supportTagLabel: Record<string, string> = {
  supports:           'Supports',
  partially_supports: 'Partially supports',
  contradicts:        'Contradicts',
}

export function HiddenSourcesSection({
  hiddenEvidences,
  getSourceLabel,
  onRestore,
}: HiddenSourcesSectionProps) {
  const [isOpen, setIsOpen] = useState(false)

  if (hiddenEvidences.length === 0) return null

  return (
    <div className="mt-0.5">
      <button
        type="button"
        className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider cursor-pointer flex items-center gap-1.5 py-1.5 w-full hover:text-foreground transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`text-[10px] transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}>
          ›
        </span>
        Hidden sources ({hiddenEvidences.length})
      </button>

      {isOpen && (
        <div className="space-y-1.5">
          {hiddenEvidences.map((ev, i) => {
            const tagColor = supportTagStyle[ev.support_level] ?? supportTagStyle.supports
            const tagLabel = supportTagLabel[ev.support_level] ?? 'Supports'
            const isContra = ev.support_level === 'contradicts'
            const previewText = ev.proof_passages.find((p) => p.passage_text)?.passage_text ?? ''

            return (
              <div
                key={ev.source_doc + i}
                className={`bg-muted rounded-lg opacity-55 overflow-hidden ${
                  isContra ? 'border-l-2 border-l-[var(--status-error-text)]' : ''
                }`}
              >
                <div className="flex items-start gap-1.5 px-2.5 py-2">
                  <div className="flex-1 min-w-0">
                    <div className={`text-[9px] font-semibold uppercase tracking-wide ${tagColor}`}>
                      {tagLabel}
                    </div>
                    <div className="text-[11px] font-medium text-foreground leading-tight">
                      <ReactMarkdown components={mdComponents}>{getSourceLabel(ev.source_doc)}</ReactMarkdown>
                    </div>
                    {previewText && (
                      <div className="text-[10px] text-muted-foreground truncate mt-0.5 leading-tight">
                        "{previewText}"
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="text-[10px] px-2 py-0.5 rounded-md text-primary hover:bg-primary/10 shrink-0 transition-colors"
                    onClick={() => onRestore(ev.source_doc)}
                  >
                    Restore
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
